import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Select,
  Statistic,
  Progress,
  Table,
  Tag,
  Modal,
  Alert,
  Tabs,
  List,
  Avatar,
  Badge,
  Divider,
  message
} from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  DownloadOutlined,
  ReloadOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import {
  Line,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const RevenueAnalyticsDashboard = ({ visible, onClose, filterType: _filterType }) => {
  const [_loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('6months');
  const [_dateRange, _setDateRange] = useState([moment().subtract(6, 'months'), moment()]);
  const [_viewType, _setViewType] = useState('overview');

  // Revenue Analytics Data
  const [revenueData, _setRevenueData] = useState({
    totalRevenue: 245680.50,
    monthlyRecurringRevenue: 98756.20,
    averageRevenuePerCustomer: 197.15,
    directCustomerRevenue: 178789.20,
    partnerGeneratedRevenue: 66891.30,
    growthRate: 23.7,
    churnRate: 2.3,
    customerLifetimeValue: 2456.78,
    
    // Trends
    trends: {
      totalRevenue: { change: 23.7, direction: 'up' },
      mrr: { change: 18.9, direction: 'up' },
      arpc: { change: 12.4, direction: 'up' },
      directRevenue: { change: 21.5, direction: 'up' },
      partnerRevenue: { change: 28.3, direction: 'up' },
      churn: { change: -15.2, direction: 'down' }
    }
  });

  // Monthly Revenue Trend Data
  const monthlyRevenueData = [
    { month: 'Sep 2023', directCustomers: 45678.90, partnerRevenue: 12345.67, totalRevenue: 58024.57, customers: 156, newCustomers: 15 },
    { month: 'Oct 2023', directCustomers: 52345.78, partnerRevenue: 15678.90, totalRevenue: 68024.68, customers: 167, newCustomers: 18 },
    { month: 'Nov 2023', directCustomers: 48567.89, partnerRevenue: 18901.23, totalRevenue: 67469.12, customers: 178, newCustomers: 21 },
    { month: 'Dec 2023', directCustomers: 56789.01, partnerRevenue: 21234.56, totalRevenue: 78023.57, customers: 189, newCustomers: 19 },
    { month: 'Jan 2024', directCustomers: 62345.67, partnerRevenue: 23456.78, totalRevenue: 85802.45, customers: 203, newCustomers: 24 },
    { month: 'Feb 2024', directCustomers: 68901.23, partnerRevenue: 26789.01, totalRevenue: 95690.24, customers: 218, newCustomers: 27 },
    { month: 'Mar 2024', directCustomers: 75456.78, partnerRevenue: 29012.34, totalRevenue: 104469.12, customers: 235, newCustomers: 31 }
  ];

  // Revenue by Customer Plan
  const planRevenueData = [
    { plan: 'Enterprise', revenue: 89456.78, customers: 45, percentage: 36.4, color: '#722ed1' },
    { plan: 'Professional', revenue: 67890.12, customers: 78, percentage: 27.6, color: '#1890ff' },
    { plan: 'Business', revenue: 54321.09, customers: 156, percentage: 22.1, color: '#52c41a' },
    { plan: 'Basic', revenue: 34012.51, customers: 234, percentage: 13.9, color: '#fa8c16' }
  ];

  // Revenue by Partner Tier
  const partnerTierData = [
    { tier: 'Platinum', revenue: 23456.78, partners: 4, customers: 78, percentage: 35.1, color: '#722ed1' },
    { tier: 'Gold', revenue: 18790.12, partners: 12, customers: 156, percentage: 28.1, color: '#faad14' },
    { tier: 'Silver', revenue: 15234.56, partners: 18, customers: 98, percentage: 22.8, color: '#8c8c8c' },
    { tier: 'Bronze', revenue: 9410.84, partners: 8, customers: 45, percentage: 14.0, color: '#d4734b' }
  ];

  // Payment Status Data
  const paymentStatusData = [
    { status: 'Paid', amount: 187654.32, count: 445, percentage: 76.4, color: '#52c41a' },
    { status: 'Pending', amount: 34567.89, count: 89, percentage: 14.1, color: '#faad14' },
    { status: 'Overdue', amount: 12345.67, count: 23, percentage: 5.0, color: '#ff4d4f' },
    { status: 'Disputed', amount: 8765.43, count: 12, percentage: 3.6, color: '#722ed1' },
    { status: 'Refunded', amount: 2347.19, count: 8, percentage: 0.9, color: '#8c8c8c' }
  ];

  // Top Revenue Customers
  const topCustomers = [
    { id: 1, company: 'TechCorp Enterprise', revenue: 12456.78, growth: 24.5, plan: 'Enterprise', messages: 45678 },
    { id: 2, company: 'Global Healthcare Ltd', revenue: 9876.54, growth: 18.9, plan: 'Enterprise', messages: 34567 },
    { id: 3, company: 'RetailChain Plus', revenue: 8765.43, growth: 32.1, plan: 'Professional', messages: 28901 },
    { id: 4, company: 'FinanceGroup Inc', revenue: 7654.32, growth: 15.7, plan: 'Professional', messages: 23456 },
    { id: 5, company: 'EduTech Solutions', revenue: 6543.21, growth: 41.2, plan: 'Business', messages: 19876 }
  ];

  // Commission Analysis
  const commissionData = [
    { month: 'Jan 2024', totalCommission: 4567.89, paidCommission: 4567.89, pendingCommission: 0, partners: 38 },
    { month: 'Feb 2024', totalCommission: 5234.56, paidCommission: 5234.56, pendingCommission: 0, partners: 40 },
    { month: 'Mar 2024', totalCommission: 5891.23, paidCommission: 4567.89, pendingCommission: 1323.34, partners: 42 }
  ];

  useEffect(() => {
    fetchRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      message.error('Failed to fetch revenue data');
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const renderTrend = (trend) => {
    const Icon = trend.direction === 'up' ? ArrowUpOutlined : ArrowDownOutlined;
    const color = trend.direction === 'up' ? '#52c41a' : '#ff4d4f';
    
    return (
      <Space style={{ color }}>
        <Icon />
        <Text style={{ color, fontSize: '12px' }}>{Math.abs(trend.change)}%</Text>
      </Space>
    );
  };

  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#fa541c', '#13c2c2', '#eb2f96', '#f759ab'];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <DollarOutlined style={{ color: '#52c41a' }} />
            <span>Revenue Analytics Dashboard</span>
          </Space>
          <Space>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: 120 }}
              size="small"
            >
              <Option value="3months">3 Months</Option>
              <Option value="6months">6 Months</Option>
              <Option value="1year">1 Year</Option>
              <Option value="custom">Custom</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchRevenueData} size="small">
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} size="small">
              Export
            </Button>
          </Space>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={1600}
      footer={null}
      style={{ top: 20 }}
    >
      <Tabs defaultActiveKey="overview" type="card">
        <TabPane tab="Revenue Overview" key="overview">
          {/* Key Metrics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={revenueData.totalRevenue}
                  precision={2}
                  prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                  suffix={renderTrend(revenueData.trends.totalRevenue)}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Monthly Recurring Revenue"
                  value={revenueData.monthlyRecurringRevenue}
                  precision={2}
                  prefix={<BankOutlined style={{ color: '#1890ff' }} />}
                  suffix={renderTrend(revenueData.trends.mrr)}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Average Revenue Per Customer"
                  value={revenueData.averageRevenuePerCustomer}
                  precision={2}
                  prefix={<UserOutlined style={{ color: '#722ed1' }} />}
                  suffix={renderTrend(revenueData.trends.arpc)}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Customer Lifetime Value"
                  value={revenueData.customerLifetimeValue}
                  precision={2}
                  prefix={<TrophyOutlined style={{ color: '#fa8c16' }} />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Revenue Trend Chart */}
          <Card title="Revenue Trend Analysis" style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip
                  formatter={(value, name) => {
                    if (name === 'newCustomers') return [value, 'New Customers'];
                    return [formatCurrency(value), name];
                  }}
                />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="directCustomers"
                  stackId="1"
                  stroke="#1890ff"
                  fill="#1890ff"
                  fillOpacity={0.6}
                  name="Direct Customers Revenue"
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="partnerRevenue"
                  stackId="1"
                  stroke="#52c41a"
                  fill="#52c41a"
                  fillOpacity={0.6}
                  name="Partner Revenue"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="newCustomers"
                  stroke="#722ed1"
                  strokeWidth={3}
                  name="New Customers"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Revenue Distribution */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card title="Revenue by Customer Plan">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={planRevenueData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="revenue"
                      label={({ plan, percentage }) => `${plan} (${percentage}%)`}
                    >
                      {planRevenueData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Partner Revenue by Tier">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={partnerTierData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="tier" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="revenue" fill="#8884d8">
                      {partnerTierData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Customer Revenue" key="customer-revenue">
          {/* Direct vs Partner Revenue */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Direct Customer Revenue"
                  value={revenueData.directCustomerRevenue}
                  precision={2}
                  prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                  suffix={renderTrend(revenueData.trends.directRevenue)}
                  valueStyle={{ color: '#1890ff' }}
                />
                <Progress
                  percent={Math.round((revenueData.directCustomerRevenue / revenueData.totalRevenue) * 100)}
                  strokeColor="#1890ff"
                  style={{ marginTop: 8 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Partner Generated Revenue"
                  value={revenueData.partnerGeneratedRevenue}
                  precision={2}
                  prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                  suffix={renderTrend(revenueData.trends.partnerRevenue)}
                  valueStyle={{ color: '#52c41a' }}
                />
                <Progress
                  percent={Math.round((revenueData.partnerGeneratedRevenue / revenueData.totalRevenue) * 100)}
                  strokeColor="#52c41a"
                  style={{ marginTop: 8 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Top Revenue Customers */}
          <Card title="Top Revenue Generating Customers" style={{ marginBottom: 24 }}>
            <List
              dataSource={topCustomers}
              renderItem={(customer, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        size="large"
                      >
                        #{index + 1}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text strong>{customer.company}</Text>
                        <Tag color={customer.plan === 'Enterprise' ? 'purple' : 
                                   customer.plan === 'Professional' ? 'blue' : 'green'}>
                          {customer.plan}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space split={<Divider type="vertical" />}>
                        <Text>{formatCurrency(customer.revenue)} monthly</Text>
                        <Text>{customer.messages.toLocaleString()} messages</Text>
                        <Text style={{ color: '#52c41a' }}>
                          <ArrowUpOutlined /> {customer.growth}% growth
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          {/* Plan Revenue Details */}
          <Card title="Revenue Breakdown by Plan">
            <Table
              dataSource={planRevenueData}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Plan',
                  dataIndex: 'plan',
                  key: 'plan',
                  render: (plan, record) => (
                    <Space>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: record.color,
                          borderRadius: 2
                        }}
                      />
                      <Text strong>{plan}</Text>
                    </Space>
                  )
                },
                {
                  title: 'Revenue',
                  dataIndex: 'revenue',
                  key: 'revenue',
                  render: (revenue) => <Text strong>{formatCurrency(revenue)}</Text>
                },
                {
                  title: 'Customers',
                  dataIndex: 'customers',
                  key: 'customers'
                },
                {
                  title: 'Percentage',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  render: (percentage, record) => (
                    <div>
                      <Progress
                        percent={percentage}
                        size="small"
                        strokeColor={record.color}
                        format={(percent) => `${percent}%`}
                      />
                    </div>
                  )
                },
                {
                  title: 'Avg per Customer',
                  key: 'avgPerCustomer',
                  render: (_, record) => formatCurrency(record.revenue / record.customers)
                }
              ]}
            />
          </Card>
        </TabPane>

        <TabPane tab="Payments & Billing" key="payments">
          {/* Payment Status Overview */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card title="Payment Status Distribution">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={paymentStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="amount"
                      label={({ status, percentage }) => `${status} (${percentage}%)`}
                    >
                      {paymentStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Payment Status Details">
                <List
                  dataSource={paymentStatusData}
                  renderItem={(payment) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Badge
                            color={payment.color}
                            style={{ marginRight: 8 }}
                          />
                        }
                        title={
                          <Space>
                            <Text strong>{payment.status}</Text>
                            <Tag color={payment.color}>{payment.count} customers</Tag>
                          </Space>
                        }
                        description={
                          <Space>
                            <Text>{formatCurrency(payment.amount)}</Text>
                            <Text type="secondary">({payment.percentage}%)</Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>

          {/* Outstanding Payments Alert */}
          <Alert
            message="Payment Attention Required"
            description={`${paymentStatusData.find(p => p.status === 'Overdue')?.count || 0} customers have overdue payments totaling ${formatCurrency(paymentStatusData.find(p => p.status === 'Overdue')?.amount || 0)}`}
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
            action={
              <Button size="small" type="primary">
                Review Overdue
              </Button>
            }
          />

          {/* Commission Tracking */}
          <Card title="Partner Commission Tracking" style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={commissionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="paidCommission" stackId="a" fill="#52c41a" name="Paid Commission" />
                <Bar dataKey="pendingCommission" stackId="a" fill="#faad14" name="Pending Commission" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabPane>

        <TabPane tab="Performance Metrics" key="performance">
          {/* Key Performance Indicators */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Revenue Growth Rate"
                  value={revenueData.growthRate}
                  precision={1}
                  suffix="%"
                  prefix={<RiseOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Customer Churn Rate"
                  value={revenueData.churnRate}
                  precision={1}
                  suffix="%"
                  prefix={<FallOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Partner Contribution"
                  value={Math.round((revenueData.partnerGeneratedRevenue / revenueData.totalRevenue) * 100)}
                  suffix="%"
                  prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Performance Insights */}
          <Card title="Revenue Performance Insights">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Alert
                  message="Strong Partner Performance"
                  description="Partner-generated revenue has grown by 28.3% this month, indicating effective partner program."
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Alert
                  message="Enterprise Customer Growth"
                  description="Enterprise customers show the highest ARPC at $1,987 with 24.5% month-over-month growth."
                  type="info"
                  showIcon
                />
              </Col>
              <Col xs={24} md={12}>
                <Alert
                  message="Churn Rate Alert"
                  description="Customer churn rate is at 2.3%. Monitor at-risk customers to prevent further losses."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Alert
                  message="Payment Collection"
                  description="5.0% of revenue is in overdue status. Focus on payment collection efforts."
                  type="error"
                  showIcon
                />
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default RevenueAnalyticsDashboard;
